import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLoaderData } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType, { ratesTransformer } from '@r6digital/rapidstor-types/react/entities/UnitType';
import Rate from '@r6digital/rapidstor-types/types/Rate';

import { useGetCouponRatesMutation } from '@/core/api/rapidstor';
import Logger from '@/core/utils/logging';

import store from '@/store';
import {
    selectReservationData,
    selectUnitData,
    setReservationData,
    setUnitData,
} from '@/store/bookingSlice';

import Spinner from '@/core/components/elements/spinner';

import { ANIMATION_SPEED } from '@/features/rapidstor/animations';

import classes from './styles.module.scss';
import IconCoupon from '@/assets/icon-coupon.svg?react';

export type Styling = 'base' | 'primary' | 'merch';

export interface CouponAlert {
    text: string;
    type: 'error' | 'success';
}

interface Props {
    type: Styling;
}

export default function Coupon({ type }: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const [code, setCode] = useState<string>('');
    const [activeCode, setActiveCode] = useState<string>('');
    const [alert, setAlert] = useState<CouponAlert | null>(null);
    const [fetchCouponRates] = useGetCouponRatesMutation();
    const [loading, setLoading] = useState<boolean>(false);

    const reservationData = useSelector(selectReservationData);
    const unitData = useSelector(selectUnitData);

    const getCouponRates = async (couponCode: string, unitTypeId: string) => {
        const result = await fetchCouponRates({
            couponCode,
            unitTypeId,
            corpCode: location.corpCode,
            locationCode: location.locationCode,
        }).unwrap();

        return result;
    };

    const handleError = () => {
        setActiveCode(code);
        setLoading(false);
        setAlert({
            text: t('coupon_code:alerts:error'),
            type: 'error',
        });
    };

    const postCouponApply = async () => {
        if (!reservationData || !unitData) {
            return;
        }

        setLoading(true);

        try {
            const result = await getCouponRates(code, reservationData.unitType._id);

            if (result.deal) {
                const updatedUnitType = {
                    ...reservationData.unitType,
                    deal: result.deal,
                    // Raw Rate from the API -> the friendly Rates entity UnitType holds.
                    rates: ratesTransformer(result.rates),
                };

                const { descriptor } = reservationData;
                const newUnitTypes: UnitType[] = [];

                for (let unitType of descriptor.unitTypes) {
                    // eslint-disable-next-line no-await-in-loop
                    const unitTypeWithCoupon = await getCouponRates(code, unitType._id);
                    unitType = {
                        ...unitType,
                        deal: unitTypeWithCoupon.deal,
                        // The API returns the raw Rate; UnitType holds the friendly Rates
                        // entity, so it has to go through the transformer.
                        rates: ratesTransformer(unitTypeWithCoupon.rates),
                    };
                    newUnitTypes.push(unitType);
                }

                const newDescriptor = {
                    ...descriptor,
                    unitTypes: newUnitTypes,
                };

                const newDeal = {
                    ...result.deal,
                    rates: result.rates as Rate,
                };

                const newReservationData = {
                    ...reservationData,
                    deal: result.deal._id as string,
                    dealObject: newDeal,
                    unitType: updatedUnitType,
                    descriptor: newDescriptor,
                    coupon: code,
                };

                const newUnitData = {
                    ...unitData,
                    unitType: updatedUnitType,
                };

                store.dispatch(setUnitData(newUnitData));
                store.dispatch(setReservationData(newReservationData));
                setAlert({
                    text: t('coupon_code:alerts:success'),
                    type: 'success',
                });
                setLoading(false);
                setActiveCode(code);
            } else {
                setLoading(false);
            }
        } catch (error) {
            Logger.warn(
                `[postCouponApply] Failed to apply coupon code: ${JSON.stringify(error)}`,
                location,
                [
                    {
                        name: 'coupon_code',
                        value: code,
                    },
                ],
            );
            handleError();
        }
    };

    useEffect(() => {
        if (reservationData && reservationData.coupon === null) {
            setCode('');
            setActiveCode('');
            setAlert(null);
        }
    }, [reservationData]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
                duration: ANIMATION_SPEED,
                delay: ANIMATION_SPEED,
            }}
            className={`${classes.container} ${type === 'base' ? classes.base : classes.primary
            }`}
        >
            <div className={`${classes.header}`}>
                <div className={`${classes.left}`}>
                    <IconCoupon />
                    <span>{t('coupon_code:title')}</span>
                </div>
                <div className={`${classes.right}`}>
                    {alert && (
                        <span
                            className={`${alert.type === 'error' ? classes.error : classes.success
                            }`}
                        >
                            {alert.text}
                        </span>
                    )}
                </div>
            </div>
            <div className={`${classes.body}`}>
                <input
                    type="text"
                    placeholder={t('coupon_code:placeholder')}
                    onChange={(e) => setCode(e.target.value)}
                    value={code}
                    disabled={loading}
                    className={classes.input}
                />
                <button
                    type="button"
                    disabled={loading || code === activeCode}
                    className={classes.button}
                    onClick={() => {
                        if (code !== '') {
                            postCouponApply();
                        }
                    }}
                >
                    {loading ? <Spinner size="20" /> : t('coupon_code:apply')}
                </button>
            </div>
        </motion.div>
    );
}
